/* eslint-disable max-nested-callbacks */
// eslint-disable-next-line @altermaind/you-shall-not-pass
import { ReactNode, useEffect } from 'react';
import { OAuthPopup } from '@tasoskakour/react-use-oauth2';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes as Routing,
  useLocation,
} from 'react-router-dom';
import { BlockingDialogs } from '@/_shared/components/BlockingDialogs';
import { GenericDialog } from '@/_shared/components/GenericDialog';
import { WarningDialogs } from '@/_shared/components/WarningDialogs';
import { AssessmentDetailScreen } from '@/assessments/screens';
import { ConfigurationScreen } from '@/configuration/screens';
import { ConversationScreen, ConversationsScreen } from '@/conversations/screens';
import { ComponentsScreen } from '@/core/screens';
import { getOrganizationConfigCall, getUserProfileCall } from '@/core/state/api';
import { DashboardScreen } from '@/dashboard/screens';
import {
  DepartmentScreen,
  DepartmentEmployeeDetailsScreen,
  TeamScreen,
  TeamEmployeeDetailsScreen,
} from '@/department/screens';
import {
  DepartmentsDetailScreen,
  DepartmentsScreen,
  DepartmentsTeamsDetailScreen,
} from '@/departments/screens';
import { ConfirmEmailScreen, SetPasswordScreen } from '@/invitation/screens';
import { LoginSSOScreen, LoginScreen } from '@/login/screens';
import { useLogin } from '@/login/state';
import { MaintenanceModeScreen } from '@/maintenanceMode';
import {
  PeopleDetailsScreen,
  PeopleImportScreen,
  PeopleScreen,
  PeopleReviewScreen,
} from '@/people/screens';
import { getEmployeesMeCall } from '@/profile';
import { ProfileScreen } from '@/profile/screens';
import { ReportsAssessmentsScreen, ReportsTrainingsScreen } from '@/reports/screens';
import { SettingsScreen } from '@/settings/screens';
import {
  SimulateSurveyScreen,
  SurveyDetailsScreen,
  SurveysScreen,
  SurveyToCompleteScreen,
} from '@/surveys/screens';
import { getEmployerNotCompletedSurveysCall } from '@/surveys/state/api';
import { AiPlaygroundScreen } from '@/training/aiPlayground/screens';
import { DocumentScreen } from '@/training/documents/screens';
import {
  TrainingHardSkillsScreen,
  MyContentsScreen,
  SpecificContentsScreen,
} from '@/training/hardSkills/screens';
import { QuizScreen } from '@/training/quiz/screens';
import {
  TrainingSoftSkillsScreen,
  MyContentsScreen as SoftMyContentsScreen,
  SpecificContentsScreen as SoftSpecificContentsScreen,
} from '@/training/softSkills/screens';
import { VideoScreen } from '@/training/video/screens';
import {
  PageLayout,
  FallbackSuccess,
  ToggleDevPanel,
  ScrollToTop,
  Chatbot,
  ClaimsComponent,
} from '../components';
import {
  Claims,
  FeatureFlag,
  PLATFORM_TYPE_FROM_ROLE,
  PlatformType,
  Roles,
  SuccessCodes,
} from '../constants';
import { Routes as RoutesConstants } from '../constants/routes';
import { getEnvironment } from '../helpers';
import { useFeatureFlags } from '../hooks';

const RoutesWithAuth = ({ children }: { children: ReactNode }) => {
  const { isUserLogged } = useLogin();

  if (!isUserLogged) {
    return <Navigate to={RoutesConstants.Login} replace />;
  }
  return children;
};

const RoutesWithPageLayout = () => {
  return (
    <RoutesWithAuth>
      <PageLayout>
        <Outlet />
      </PageLayout>
    </RoutesWithAuth>
  );
};

const RoutesForConfigurationPlatform = () => {
  const { userMustConfigureOrganization } = getOrganizationConfigCall.hook();
  const { userHasSurveysToComplete } = getEmployerNotCompletedSurveysCall.hook();
  const { userRole, firstLogin } = getUserProfileCall.hook();

  const hasCompletedFirstLogin = firstLogin !== undefined && firstLogin !== '';

  if (
    hasCompletedFirstLogin &&
    PLATFORM_TYPE_FROM_ROLE[userRole as never] === PlatformType.LEARNING
  ) {
    return <Navigate to={RoutesConstants.Homepage} replace />;
  }

  if (hasCompletedFirstLogin && !userMustConfigureOrganization && !userHasSurveysToComplete) {
    return <Navigate to={RoutesConstants.Homepage} replace />;
  }

  return (
    <RoutesWithAuth>
      <Outlet />
    </RoutesWithAuth>
  );
};

const RoutesMustBeConfigured = () => {
  const { firstLogin, hasSkippedConfig } = getUserProfileCall.hook();
  const { userMustConfigureOrganization } = getOrganizationConfigCall.hook();

  const isUserFirstLogin = firstLogin === undefined || firstLogin === '';

  if (isUserFirstLogin && userMustConfigureOrganization && !hasSkippedConfig) {
    return <Navigate to={RoutesConstants.ConfigurePlatform} replace />;
  }

  return <Outlet />;
};

const RoutesOnlyForLearningPlatform = () => {
  const { userRole } = getUserProfileCall.hook();

  if (!userRole || PLATFORM_TYPE_FROM_ROLE[userRole as never] !== PlatformType.LEARNING) {
    return <Navigate to={RoutesConstants.Homepage} replace />;
  }

  return <Outlet />;
};

const RoutesForSpecificManagerOfDepartment = () => {
  const { hasManagerLeader } = getEmployeesMeCall.hook();

  if (!hasManagerLeader) {
    return <Navigate to={RoutesConstants.Homepage} replace />;
  }

  return <Outlet />;
};

const RoutesForSpecificTeamLeader = () => {
  const { hasTeamLeader } = getEmployeesMeCall.hook();

  if (!hasTeamLeader) {
    return <Navigate to={RoutesConstants.Homepage} replace />;
  }

  return <Outlet />;
};

const RoutesWithoutAuth = () => {
  const { isUserLogged } = useLogin();

  if (isUserLogged) {
    return <Navigate to={RoutesConstants.Homepage} replace />;
  }
  return <Outlet />;
};

const RoutesInvitation = () => {
  const { isUserLogged, resetSession } = useLogin();

  useEffect(() => {
    if (isUserLogged) {
      resetSession();
    }
  }, [isUserLogged, resetSession]);

  return <Outlet />;
};

const RoutesWithMaintenance = () => {
  const { isFeatureEnabled } = useFeatureFlags();
  const location = useLocation();
  const isMaintenanceModeOn = isFeatureEnabled(FeatureFlag.MAINTENANCE_MODE);
  const isMaintenanceRoute = location.pathname === RoutesConstants.Maintenance;

  if (isMaintenanceRoute) {
    if (!isMaintenanceModeOn) {
      return <Navigate to={RoutesConstants.Homepage} replace />;
    }
    return <Outlet />;
  }

  if (isMaintenanceModeOn) {
    return <Navigate to={RoutesConstants.Maintenance} replace />;
  }

  return <Outlet />;
};

// Escape hatch for the static sites served on this same hostname (/docs, /website): browsers
// still controlled by an old SW land here on the SPA shell, so we unregister the SW, clear its
// caches and reload to let the navigation reach the real site.
const ServiceWorkerEscapeHatch = () => {
  useEffect(() => {
    (async () => {
      if (!('serviceWorker' in navigator)) {
        return;
      }

      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          regs.map(reg =>
            reg
              .unregister()
              .then(() => window.location.reload())
              .catch(() => false),
          ),
        );

        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(
            keys.map(k =>
              caches
                .delete(k)
                .then(() => window.location.reload())
                .catch(() => false),
            ),
          );
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (err) {
        /* empty */
      }
    })();
  }, []);

  return null;
};

const Routes = () => {
  const { isProduction } = getEnvironment();

  return (
    <BrowserRouter basename='/'>
      <ScrollToTop />
      <GenericDialog />
      <WarningDialogs />
      <BlockingDialogs />
      <Chatbot />
      <ToggleDevPanel />
      <Routing>
        <Route path={RoutesConstants.Docs} element={<ServiceWorkerEscapeHatch />} />
        <Route path={RoutesConstants.Website} element={<ServiceWorkerEscapeHatch />} />
        <Route element={<RoutesWithMaintenance />}>
          <Route path={RoutesConstants.Maintenance} element={<MaintenanceModeScreen />} />
          <Route path={RoutesConstants.OAuthPopup} element={<OAuthPopup />} />
          <Route
            path={RoutesConstants.RegisterTecnicalPage}
            element={
              <FallbackSuccess translationKey={SuccessCodes.REGISTRATION_COMPLETED} onlyClose />
            }
          />
          <Route
            path={RoutesConstants.ResetPasswordTecnicalPage}
            element={<FallbackSuccess translationKey={SuccessCodes.PASSWORD_RESET_COMPLETED} />}
          />
          <Route element={<RoutesWithoutAuth />}>
            <Route path={RoutesConstants.Login} element={<LoginScreen />} />
            <Route path={RoutesConstants.LoginSSO}>
              <Route path=':idpAlias' element={<LoginSSOScreen />} />
            </Route>
          </Route>
          <Route element={<RoutesInvitation />}>
            <Route path={RoutesConstants.SetPassword} element={<SetPasswordScreen />} />
            <Route path={RoutesConstants.ConfirmEmail} element={<ConfirmEmailScreen />} />
          </Route>
          <Route element={<RoutesForConfigurationPlatform />}>
            <Route path={RoutesConstants.ConfigurePlatform} element={<ConfigurationScreen />} />
          </Route>
          <Route element={<RoutesMustBeConfigured />}>
            <Route path={RoutesConstants.Conversation} element={<ConversationScreen />} />
            <Route
              path={RoutesConstants.Quiz}
              element={
                <ClaimsComponent claimKey={Claims.TRAINING_READ} featureFlag={FeatureFlag.TRAINING}>
                  <QuizScreen />
                </ClaimsComponent>
              }
            />
            <Route
              path={RoutesConstants.Video}
              element={
                <ClaimsComponent claimKey={Claims.TRAINING_READ} featureFlag={FeatureFlag.TRAINING}>
                  <VideoScreen />
                </ClaimsComponent>
              }
            />
            <Route
              path={RoutesConstants.Document}
              element={
                <ClaimsComponent claimKey={Claims.TRAINING_READ} featureFlag={FeatureFlag.TRAINING}>
                  <DocumentScreen />
                </ClaimsComponent>
              }
            />
            <Route path={RoutesConstants.SurveyToComplete} element={<SurveyToCompleteScreen />} />
            <Route element={<RoutesWithPageLayout />}>
              {!isProduction && <Route path='/components' element={<ComponentsScreen />} />}
              <Route
                path={RoutesConstants.Homepage}
                element={
                  <ClaimsComponent
                    claimKey={Claims.DASHBOARD_READ}
                    featureFlag={FeatureFlag.DASHBOARD}
                  >
                    <DashboardScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.People}
                element={
                  <ClaimsComponent
                    roles={[Roles.Admin, Roles.HR_Delegate]}
                    claimKey={Claims.PEOPLE_READ}
                    featureFlag={FeatureFlag.PEOPLE}
                  >
                    <PeopleScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.PeopleImport}
                element={
                  <ClaimsComponent
                    roles={[Roles.Admin, Roles.HR_Delegate]}
                    claimKey={Claims.PEOPLE_READ}
                    featureFlag={FeatureFlag.PEOPLE}
                  >
                    <PeopleImportScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.PeopleReview}
                element={
                  <ClaimsComponent
                    roles={[Roles.Admin, Roles.HR_Delegate]}
                    claimKey={Claims.PEOPLE_READ}
                    featureFlag={FeatureFlag.PEOPLE}
                  >
                    <PeopleReviewScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.PeopleDetail}
                element={
                  <ClaimsComponent
                    roles={[Roles.Admin, Roles.HR_Delegate]}
                    claimKey={Claims.PEOPLE_READ}
                    featureFlag={FeatureFlag.PEOPLE}
                  >
                    <PeopleDetailsScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.Settings}
                element={
                  <ClaimsComponent
                    claimKey={[Claims.ASPECT_READ, Claims.ORGANIZATION_READ]}
                    featureFlag={[FeatureFlag.ASPECT, FeatureFlag.ORGANIZATION]}
                    featureFlagCheckMode='some'
                    roles={[Roles.Admin, Roles.HR_Delegate]}
                  >
                    <SettingsScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.SurveyDetail}
                element={
                  <ClaimsComponent
                    claimKey={Claims.SURVEYS_WRITE}
                    featureFlag={FeatureFlag.SURVEY}
                    roles={[Roles.Admin, Roles.HR_Delegate]}
                  >
                    <SurveysScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.SurveyDetailsStats}
                element={
                  <ClaimsComponent
                    claimKey={Claims.SURVEYS_WRITE}
                    featureFlag={FeatureFlag.SURVEY}
                    roles={[Roles.Admin, Roles.HR_Delegate]}
                  >
                    <SurveyDetailsScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.SurveySimulate}
                element={
                  <ClaimsComponent
                    claimKey={Claims.SURVEYS_WRITE}
                    featureFlag={FeatureFlag.SURVEY}
                    roles={[Roles.Admin, Roles.HR_Delegate]}
                  >
                    <SimulateSurveyScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.AiPlayground}
                element={
                  <ClaimsComponent
                    claimKey={Claims.TRAINING_READ}
                    featureFlag={FeatureFlag.TRAINING}
                  >
                    <AiPlaygroundScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.TrainingSoftSkills}
                element={
                  <ClaimsComponent
                    claimKey={Claims.TRAINING_READ}
                    featureFlag={FeatureFlag.TRAINING}
                  >
                    <TrainingSoftSkillsScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.TrainingSoftSkillsMyContents}
                element={
                  <ClaimsComponent
                    claimKey={Claims.TRAINING_READ}
                    featureFlag={FeatureFlag.TRAINING}
                    roles={[Roles.Employee, Roles.Manager]}
                  >
                    <SoftMyContentsScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.TrainingSoftSkillsSpecificContents}
                element={
                  <ClaimsComponent
                    claimKey={Claims.TRAINING_READ}
                    featureFlag={FeatureFlag.TRAINING}
                  >
                    <SoftSpecificContentsScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.TrainingHardSkills}
                element={
                  <ClaimsComponent
                    claimKey={Claims.TRAINING_READ}
                    featureFlag={FeatureFlag.TRAINING}
                  >
                    <TrainingHardSkillsScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.TrainingHardSkillsMyContents}
                element={
                  <ClaimsComponent
                    claimKey={Claims.TRAINING_READ}
                    featureFlag={FeatureFlag.TRAINING}
                    roles={[Roles.Employee, Roles.Manager]}
                  >
                    <MyContentsScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.TrainingHardSkillsSpecificContents}
                element={
                  <ClaimsComponent
                    claimKey={Claims.TRAINING_READ}
                    featureFlag={FeatureFlag.TRAINING}
                  >
                    <SpecificContentsScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.Departments}
                element={
                  <ClaimsComponent
                    claimKey={Claims.DEPARTMENTS_READ}
                    featureFlag={FeatureFlag.DEPARTMENTS}
                    roles={[Roles.Admin, Roles.HR_Delegate]}
                  >
                    <DepartmentsScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.DepartmentsDetail}
                element={
                  <ClaimsComponent
                    claimKey={Claims.DEPARTMENTS_READ}
                    featureFlag={FeatureFlag.DEPARTMENTS}
                    roles={[Roles.Admin, Roles.HR_Delegate]}
                  >
                    <DepartmentsDetailScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.DepartmentsTeamsDetail}
                element={
                  <ClaimsComponent
                    claimKey={Claims.DEPARTMENTS_READ}
                    featureFlag={FeatureFlag.DEPARTMENTS}
                    roles={[Roles.Admin, Roles.HR_Delegate]}
                  >
                    <DepartmentsTeamsDetailScreen />
                  </ClaimsComponent>
                }
              />
              <Route element={<RoutesOnlyForLearningPlatform />}>
                <Route element={<RoutesForSpecificManagerOfDepartment />}>
                  <Route
                    path={RoutesConstants.Department}
                    element={
                      <ClaimsComponent
                        claimKey={Claims.DEPARTMENT_READ}
                        featureFlag={FeatureFlag.DEPARTMENT}
                        roles={[Roles.Manager]}
                      >
                        <DepartmentScreen />
                      </ClaimsComponent>
                    }
                  />
                  <Route
                    path={RoutesConstants.DepartmentTeamDetails}
                    element={
                      <ClaimsComponent
                        claimKey={Claims.DEPARTMENT_READ}
                        featureFlag={FeatureFlag.DEPARTMENT}
                        roles={[Roles.Manager]}
                      >
                        <TeamScreen />
                      </ClaimsComponent>
                    }
                  />
                  <Route
                    path={RoutesConstants.DepartmentEmployeeDetails}
                    element={
                      <ClaimsComponent
                        claimKey={Claims.DEPARTMENT_READ}
                        featureFlag={FeatureFlag.DEPARTMENT}
                        roles={[Roles.Manager]}
                      >
                        <DepartmentEmployeeDetailsScreen />
                      </ClaimsComponent>
                    }
                  />
                </Route>
                <Route element={<RoutesForSpecificTeamLeader />}>
                  <Route
                    path={RoutesConstants.Team}
                    element={
                      <ClaimsComponent
                        claimKey={Claims.DEPARTMENT_READ}
                        featureFlag={FeatureFlag.DEPARTMENT}
                        roles={[Roles.Manager]}
                      >
                        <TeamScreen />
                      </ClaimsComponent>
                    }
                  />
                  <Route
                    path={RoutesConstants.TeamEmployeeDetails}
                    element={
                      <ClaimsComponent
                        claimKey={Claims.DEPARTMENT_READ}
                        featureFlag={FeatureFlag.DEPARTMENT}
                        roles={[Roles.Manager]}
                      >
                        <TeamEmployeeDetailsScreen />
                      </ClaimsComponent>
                    }
                  />
                </Route>
                <Route
                  path={RoutesConstants.Profile}
                  element={
                    <ClaimsComponent
                      claimKey={Claims.PROFILE_READ}
                      featureFlag={FeatureFlag.PROFILE}
                      roles={[Roles.Manager, Roles.Employee]}
                    >
                      <ProfileScreen />
                    </ClaimsComponent>
                  }
                />
                <Route
                  path={RoutesConstants.AssessmentDetail}
                  element={
                    <ClaimsComponent
                      claimKey={Claims.DASHBOARD_READ}
                      featureFlag={FeatureFlag.ASSESSMENT}
                      roles={[Roles.Manager, Roles.Employee]}
                    >
                      <AssessmentDetailScreen />
                    </ClaimsComponent>
                  }
                />
              </Route>
              <Route
                path={RoutesConstants.ReportsAssessments}
                element={
                  <ClaimsComponent
                    featureFlag={FeatureFlag.REPORTS}
                    roles={[Roles.Admin, Roles.HR_Delegate]}
                  >
                    <ReportsAssessmentsScreen />
                  </ClaimsComponent>
                }
              />
              <Route
                path={RoutesConstants.ReportsTrainings}
                element={
                  <ClaimsComponent
                    featureFlag={FeatureFlag.REPORTS}
                    roles={[Roles.Admin, Roles.HR_Delegate]}
                  >
                    <ReportsTrainingsScreen />
                  </ClaimsComponent>
                }
              />
              <Route path={RoutesConstants.Conversations} element={<ConversationsScreen />} />
            </Route>
          </Route>
        </Route>
      </Routing>
    </BrowserRouter>
  );
};

export default Routes;
